import { AxiosResponse } from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "../../../Services/Requisition/RequisitionTypeService";
import DatePickerDuplicate from "../../../Shared/Common/DatePicker/DatePickerDuplicate";
import { reactSelectSMStyle, styles } from "../../../Utils/Common";
import BreadCrumbs from "../../../Utils/Common/Breadcrumb";
import { validateFields } from "../Toxicology/CommonFunctions";
import TestsSelection from "./TestsSelection";
import {
  GetTestsLookup,
  GetTOXAndBloodInstrumentsLookup,
  GetToxPanelTypes,
  GetTOXSpecimenTypesLookup,
  LeveyJenningDownloadReport,
  QCLevelsLookup,
} from "Services/ToxLeveyJennings";
import Radio from "Shared/Common/Input/Radio";
import PermissionComponent, { getPermissionDisplayName } from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { StringRecord } from "Shared/Type";

interface ToxLeveyJenningsValues {
  fromDate: string;
  toDate: string;
  reqTypeId: number;
  instrumentId: number;
  panelTypeId: number;
  specimenTypeId: number;
  qcLevelIds: number[];
  testConfigIds: number[];
}
const ToxLeveyJenning = () => {
  const { t } = useLang();

  const initialToxLeveyJenning = {
    fromDate: "",
    toDate: "",
    reqTypeId: 0,
    instrumentId: 0,
    panelTypeId: 0,
    specimenTypeId: 0,
    qcLevelIds: [],
    testConfigIds: [],
  };

  const queryDisplayTagNames: StringRecord = {
    fromDate: "Start Date",
    toDate: "End Date",
    reqTypeId: "Requisition Type",
    instrumentId: "Instrument",
    panelTypeId: "Test Type",
    specimenTypeId: "Specimen Types",
    qcLevelIds: "QC Level",
    testConfigIds: "Selected Analytes",
  };

  const [dropDownValues, setDropDownValues] = useState<any>({
    TestsLookup: [],
    ToxPanelLookup: [],
    QCLevelsLookup: [],
    InstrumentLookup: [],
    SpecimentTypesLookup: [],
  });
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [toxLeveyJenningsValues, setToxLeveyJenningsValues] =
    useState<ToxLeveyJenningsValues>(initialToxLeveyJenning);
  const [filterDate, setFilterDate] = useState<any>();
  //Split date
  const dateRange = filterDate ? filterDate : "";
  const [startDate, endDate] = dateRange.split("to");
  const start = moment(startDate).format("MM/DD/YYYY");
  const end = moment(endDate).format("MM/DD/YYYY");

  //LookUp Api calls Function
  const GetToxPanelTypesLookup = () => {
    GetToxPanelTypes()
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          ToxPanelLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetSpecimenTypesLookup = () => {
    GetTOXSpecimenTypesLookup()
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          SpecimentTypesLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetInstrumentsLookup = (reqtypeId: number, panelTypeId: number) => {
    GetTOXAndBloodInstrumentsLookup(reqtypeId, panelTypeId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          InstrumentLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const QCLevelsLookupApi = (
    reqTypeId: number,
    instrumentId: number,
    panelTypeId: number,
    specimenTypeId: number
  ) => {
    QCLevelsLookup(reqTypeId, instrumentId, panelTypeId, specimenTypeId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          QCLevelsLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetTestLookupApi = (
    specimenTypeId: number,
    panelId: number,
    reqTypeId: number
  ) => {
    GetTestsLookup(specimenTypeId, panelId, reqTypeId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          TestsLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const handleChange = (
    event: any,
    name?: string,
    isMulti: boolean = false
  ) => {
    // Case 1: Native or custom inputs using event.target
    if (event?.target) {
      const { name, value } = event.target;
      setToxLeveyJenningsValues((prev: any) => ({
        ...prev,
        [name]: Number(value),
      }));
      return;
    }

    // Case 2: react-select (single select)
    if (!isMulti) {
      setToxLeveyJenningsValues((prev: any) => ({
        ...prev,
        [name as string]: event?.value ?? 0,
      }));
      return;
    }

    // Case 3: react-select (multi select)
    if (isMulti) {
      setToxLeveyJenningsValues((prev: any) => ({
        ...prev,
        [name as string]: event?.map((item: any) => item.value) ?? [],
      }));
    }
  };

  //Download Button
  const DownloadReport = async () => {
    const ObjToValidate = {
      panelTypeId: toxLeveyJenningsValues?.panelTypeId,
      specimenTypeId:
        toxLeveyJenningsValues?.panelTypeId === 3
          ? true
          : toxLeveyJenningsValues?.specimenTypeId,
      instrumentId: toxLeveyJenningsValues?.instrumentId,
      qcLevelIds: toxLeveyJenningsValues?.qcLevelIds.length,
      fromDate: start === "Invalid date" ? "" : start,
      toDate: end === "Invalid date" ? "" : end,
      testConfigIds: toxLeveyJenningsValues?.testConfigIds.length,
    };

    const { isValid, invalidField } = validateFields(ObjToValidate);
    if (isValid) {
      setIsDisabled(true);
      const payload = {
        reqTypeId: 3,
        panelTypeId: toxLeveyJenningsValues?.panelTypeId,
        specimenTypeId:
          toxLeveyJenningsValues?.panelTypeId === 3
            ? 0
            : toxLeveyJenningsValues?.specimenTypeId,
        instrumentId: toxLeveyJenningsValues?.instrumentId,
        qcLevelIds: toxLeveyJenningsValues?.qcLevelIds,
        fromDate: start,
        toDate: end,
        testConfigIds: toxLeveyJenningsValues?.testConfigIds,
      };
      try {
        await LeveyJenningDownloadReport(payload).then((res: AxiosResponse) => {
          if (res?.data?.statusCode === 200) {
            toast.success(
              res?.data?.message || "Report generated successfully"
            );
            RequisitionType.ShowBlob(res?.data?.data).then((res: any) => {
              window.open(res?.data?.Data.replace("}", ""), "_blank");
            });
          } else if (res?.data?.statusCode === 400) {
            toast.error(res?.data?.message || "Error generating report");
            return;
          }
        });
      } catch (error) {
        console.trace(error);
      } finally {
        setIsDisabled(false);
      }
    } else {
      const errorMessage = invalidField
        ? t(`Please Fill ${queryDisplayTagNames[invalidField] ?? ""} field`)
        : t("Invalid field found");
      toast.error(t(errorMessage));
    }
  };

  useEffect(() => {
    GetToxPanelTypesLookup();
    GetSpecimenTypesLookup();
  }, []);

  useEffect(() => {
    const { panelTypeId, specimenTypeId } = toxLeveyJenningsValues || {};

    if (!panelTypeId) return;
    setToxLeveyJenningsValues((prev: any) => ({
      ...prev,
      testConfigIds: [],
      instrumentId: 0,
      qcLevelIds: [],
    }));
    setDropDownValues((prev: any) => ({
      ...prev,
      TestsLookup: [],
      InstrumentLookup: [],
      QCLevelsLookup: [],
    }));
    // Panel type 3 ignores specimen type
    if (panelTypeId === 3) {
      GetTestLookupApi(0, panelTypeId, 3);
      GetInstrumentsLookup(3, toxLeveyJenningsValues?.panelTypeId);
      return;
    }

    // Other panel types require specimen type
    if (specimenTypeId) {
      GetTestLookupApi(specimenTypeId, panelTypeId, 3);
      GetInstrumentsLookup(3, toxLeveyJenningsValues?.panelTypeId);
    }
  }, [
    toxLeveyJenningsValues?.panelTypeId,
    toxLeveyJenningsValues?.specimenTypeId,
  ]);

  useEffect(() => {
    const { panelTypeId, specimenTypeId, instrumentId } =
      toxLeveyJenningsValues || {};

    if (!panelTypeId) return;

    // Panel type 3 ignores specimen type
    if (panelTypeId === 3) {
      if (!instrumentId) return;
      QCLevelsLookupApi(3, instrumentId, panelTypeId, 0);
      setToxLeveyJenningsValues((prev: any) => ({
        ...prev,
        qcLevelIds: [],
      }));
      return;
    }

    // Other panel types require specimen type
    if (specimenTypeId && instrumentId) {
      QCLevelsLookupApi(3, 0, panelTypeId, specimenTypeId);
      setToxLeveyJenningsValues((prev: any) => ({
        ...prev,
        qcLevelIds: [],
      }));
    }
  }, [
    toxLeveyJenningsValues?.panelTypeId,
    toxLeveyJenningsValues?.specimenTypeId,
    toxLeveyJenningsValues?.instrumentId,
  ]);

  return (
    <>
      <div className="d-flex flex-column flex-column-fluid py-2 py-lg-3">
        <div className="app-toolbar py-2 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
          </div>
        </div>
        <div className="app-toolbar py-2 py-lg-3">
          <div className="d-flex flex-column flex-column-fluid">
            <div className="app-content flex-column-fluid">
              <div className="app-container container-fluid">
                <div className="bg-white p-6">
                  <h3 className="mb-5">{t("Report")}</h3>
                  <div className="mb-3">
                    <Radio
                      label={t("Test Type")}
                      name="panelTypeId"
                      onChange={(event) => handleChange(event, "panelTypeId")}
                      choices={dropDownValues.ToxPanelLookup}
                      checked={toxLeveyJenningsValues?.panelTypeId}
                    />
                  </div>
                  {toxLeveyJenningsValues?.panelTypeId === 2 && (
                    <div className="mb-5">
                      <Radio
                        label={t("Specimen Types")}
                        name="specimenTypeId"
                        onChange={(event) =>
                          handleChange(event, "specimenTypeId")
                        }
                        choices={dropDownValues.SpecimentTypesLookup}
                        checked={toxLeveyJenningsValues?.specimenTypeId}
                      />
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                    <div className="d-flex gap-2 responsive-flexed-actions">
                      <div className="d-flex gap-2 gap-lg-3 justify-content-center justify-content-sm-start responsive-flexed-actions flex-wrap align-items-end">
                        <div className="mt-0">
                          <label className="required mb-2 fw-500">
                            {t("Instrument")}
                          </label>
                          <Select
                            menuPortalTarget={document.body}
                            theme={(theme) => styles(theme)}
                            placeholder="Instruments"
                            options={dropDownValues.InstrumentLookup}
                            styles={reactSelectSMStyle}
                            onChange={(event: any) =>
                              handleChange(event, "instrumentId")
                            }
                            value={dropDownValues.InstrumentLookup?.filter(
                              function (option: any) {
                                return (
                                  option.value ===
                                  toxLeveyJenningsValues?.instrumentId
                                );
                              }
                            )}
                          />
                        </div>
                        <div className="mt-0" style={{ maxWidth: "30%" }}>
                          <label className="required mb-2 fw-500">
                            {t("QC Level")}
                          </label>
                          <Select
                            menuPortalTarget={document.body}
                            theme={(theme) => styles(theme)}
                            placeholder="Select QC Level"
                            options={dropDownValues.QCLevelsLookup}
                            isMulti
                            styles={reactSelectSMStyle}
                            onChange={(event: any) =>
                              handleChange(event, "qcLevelIds", true)
                            }
                            value={dropDownValues.QCLevelsLookup.filter(
                              (option: any) =>
                                toxLeveyJenningsValues.qcLevelIds.includes(
                                  option.value
                                )
                            )}
                          />
                        </div>
                        <div className="align-items-center">
                          <label className="required mb-2 fw-500">
                            {t("Date Range")}
                          </label>
                          <div>
                            <DatePickerDuplicate
                              setFilterData={setFilterDate}
                              filterData={filterDate}
                            />
                          </div>
                        </div>
                        <PermissionComponent
                          moduleName="Tox LIS"
                          pageName="Levey jenning Report"
                          permissionIdentifier="View"
                        >
                          {({ pageName, moduleName, permissionIdentifier }) => (
                            <button
                              className="btn btn-icon btn-sm fw-bold btn-warning"
                              onClick={DownloadReport}
                              disabled={isDisabled}
                              title={getPermissionDisplayName(pageName, moduleName, permissionIdentifier, t("View"))}
                            >
                              <i className="bi bi-cloud-download"></i>
                            </button>
                          )}
                        </PermissionComponent>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <TestsSelection
                    testsLookup={dropDownValues.TestsLookup}
                    setLeveyJenningsValues={setToxLeveyJenningsValues}
                    allTestsHeader="All Analytes"
                    selectedTestsHeader="Selected Analytes"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToxLeveyJenning;
